import type { DestinationCenterPoint, IAreaBoundary } from '@/shared/api/types/destination-types'
import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { DestinationMapPicker } from '@/features/destination'
import { cn } from '@/shared/libs/cn'
import { FormSectionCard } from '@/shared/ui/form-section-card'

interface MapCardProps {
  isEdit: boolean
  isReadonly: boolean
  isLoading: boolean
  areaBoundary: IAreaBoundary | null
  centerPoint: DestinationCenterPoint | null
  onAreaBoundaryChange: (areaBoundary: IAreaBoundary | null) => void
  onCenterPointChange: (centerPoint: DestinationCenterPoint | null) => void
  /** Saves boundary + centre (edit page only); the picker has its own Save button. */
  onSaveMap: (boundary: IAreaBoundary | null, center: DestinationCenterPoint | null) => void
  isSaving: boolean
}

/** Boundary polygon and centre point of the destination on a map. */
export function MapCard({
  isEdit,
  isReadonly,
  isLoading,
  areaBoundary,
  centerPoint,
  onAreaBoundaryChange,
  onCenterPointChange,
  onSaveMap,
  isSaving,
}: MapCardProps) {
  const { t } = useTranslation()

  return (
    <FormSectionCard
      title={t('destination.mapSection', { defaultValue: 'Boundary & Center Point' })}
      action={isSaving && <Loader2 className="h-4 w-4 animate-spin text-zinc-400" />}
    >
      <div className={cn(isLoading && 'opacity-60 pointer-events-none')}>
        <DestinationMapPicker
          areaBoundary={areaBoundary}
          centerPoint={centerPoint}
          onAreaBoundaryChange={onAreaBoundaryChange}
          onCenterPointChange={onCenterPointChange}
          {...(isEdit ? { onSaveMap } : {})}
          readonly={isReadonly}
          saving={isSaving}
        />
      </div>
    </FormSectionCard>
  )
}
